/**
 * Lower-right map coordinate readout.
 *
 * Shows the geographic coordinate under the cursor in the operator's
 * preferred coordinate format (REQ-UI-084 — decimal degrees / DMS / MGRS,
 * read from ``units.coordinate_format``; decimal renders latitude-first at
 * 3 decimals) in the lower-right corner of the map, mirroring the Flow 03
 * storyboard's ``.coord-readout`` placement + mono typography. The value
 * tracks the cursor and freezes on its last position when the pointer leaves
 * the map (per :func:`useCursorCoordinates`).
 *
 * Rendered nothing-until-first-sample: returns ``null`` until the cursor has
 * entered the map at least once, so an empty chip never flashes on load.
 *
 * Accessibility: marked ``aria-hidden`` — it is a pointer-only affordance
 * whose text changes on every mousemove; exposing it to assistive tech
 * would flood the live region, and keyboard users have no cursor. The map's
 * landmark + keyboard behaviour (REQ-UI-059) is unaffected.
 *
 * Cross-references:
 *   - REQ-UI-002 (MapLibre GL JS map surface).
 *   - REQ-UI-084 (coordinate-format preference; ADR-033 §7).
 *   - docs/engineering/detail/storyboards/flows/03-pan-zoom-cluster/README.md
 *     (the ``.coord-readout`` element; lower-right, mono).
 *   - docs/engineering/detail/storyboards/shared/design-system.md
 *     (mono token for coordinates).
 */
import { usePreferences } from "@/preferences/preferences-context";
import { useCursorCoordinates, type CursorMapLike } from "@/map/use-cursor-coordinates";
import { formatCoordinate } from "@/units/format-coordinate";

/** Props for {@link CursorReadout}. */
export interface CursorReadoutProps {
  /** The loaded map, or ``null`` before MapLibre's ``load`` event. */
  map: CursorMapLike | null;
}

/**
 * Render the lower-right cursor coordinate readout, or ``null`` until the
 * cursor first enters the map.
 */
export function CursorReadout({ map }: CursorReadoutProps): React.ReactElement | null {
  const coords = useCursorCoordinates(map);
  // Render in the operator's preferred coordinate format (REQ-UI-084); the
  // readout keeps its 3-decimal precision for the decimal_degrees case.
  const format = usePreferences().workingState.units?.coordinate_format ?? "decimal_degrees";
  if (coords === null) {
    return null;
  }
  return (
    <div
      aria-hidden="true"
      data-testid="map-cursor-readout"
      className="pointer-events-none absolute bottom-3 right-3 z-10 rounded border border-border bg-bg-surface px-3 py-1.5 font-mono text-meta tabular-nums text-text-secondary shadow-sm"
    >
      {formatCoordinate(coords.lat, coords.lng, format, { precision: 3 })}
    </div>
  );
}
